import { ACCOUNT_ENDPOINTS, CUSTOMER_ENDPOINTS } from "@/config/api";
import {
  ACCOUNT_TOKEN_KEY,
  CUSTOMER_TOKEN_KEY,
  SELECTED_CUSTOMER_ID_KEY,
  getAuthItem,
  removeAuthItem,
  setAuthItem,
} from "@/lib/auth-storage";
import { httpClient } from "@/lib/http-client";
import { isMaskedCustomerToken } from "@/lib/mask-session";
import type { AuthSession, AuthUser, Customer, LoginPayload, RegisterPayload } from "@/types";
import type { CustomerCreateRequest } from "@/types/customer";
import type { AccountContext, CustomerLoginResponse, ForgotPasswordPayload, RestorePasswordPayload } from "@/types/auth-api";
import type { CustomersList } from "@/types/customer";

function migrateLegacyTokens(): void {
  const legacyAccount = localStorage.getItem("accountAuthToken");
  const legacyAuth = localStorage.getItem("authToken");

  if (!getAuthItem(ACCOUNT_TOKEN_KEY) && legacyAccount) {
    setAuthItem(ACCOUNT_TOKEN_KEY, legacyAccount);
  }

  if (!getAuthItem(CUSTOMER_TOKEN_KEY) && legacyAuth) {
    const hasSelectedCustomer = getAuthItem(SELECTED_CUSTOMER_ID_KEY);
    if (hasSelectedCustomer) {
      setAuthItem(CUSTOMER_TOKEN_KEY, legacyAuth);
    } else if (!getAuthItem(ACCOUNT_TOKEN_KEY)) {
      setAuthItem(ACCOUNT_TOKEN_KEY, legacyAuth);
    }
  }
}

export class AccountsApi {
  constructor() {
    migrateLegacyTokens();
  }

  private isTokenObject(response: unknown): response is { token: string } {
    return (
      typeof response === "object" &&
      response !== null &&
      "token" in response &&
      typeof (response as { token: unknown }).token === "string" &&
      (response as { token: string }).token.length > 0
    );
  }

  private parseTokenResponse(response: CustomerLoginResponse): string {
    if (typeof response === "string" && response.length > 0) {
      return response;
    }

    if (this.isTokenObject(response)) {
      return response.token;
    }

    throw new Error("Сервер не вернул токен клиента");
  }

  // === AUTH ===

  async register(payload: RegisterPayload): Promise<Customer> {
    const body: CustomerCreateRequest = {
      email: payload.email,
      name: payload.name,
      phone: payload.phone,
      password: payload.password,
      marketing: payload.marketing ?? false,
      customerType: payload.customerType,
      ...(payload.passport ? { passport: payload.passport } : {}),
      ...(payload.company ? { company: payload.company } : {}),
      ...(payload.vat ? { vat: payload.vat } : {}),
    };

    return httpClient.post<Customer, CustomerCreateRequest>(
      CUSTOMER_ENDPOINTS.REGISTER,
      body,
      false,
    );
  }

  async login(credentials: LoginPayload): Promise<AuthSession> {
    const response = await httpClient.post<AuthSession, LoginPayload>(
      ACCOUNT_ENDPOINTS.LOGIN,
      credentials,
      false
    );

    if (response.token) {
      httpClient.setAccountToken(response.token);
    }

    return response;
  }

  async customerLogin(customerId: number): Promise<string> {
    const response = await httpClient.post<CustomerLoginResponse>(
      ACCOUNT_ENDPOINTS.CUSTOMER_LOGIN(customerId)
    );
    return this.parseTokenResponse(response);
  }

  async activateCustomerSession(customerId: number): Promise<string> {
    const customerToken = await this.customerLogin(customerId);
    httpClient.setCustomerToken(customerToken);
    setAuthItem(SELECTED_CUSTOMER_ID_KEY, String(customerId));
    return customerToken;
  }

  async getAccountContext(): Promise<AccountContext> {
    if (!this.getAccountToken()) {
      throw new Error("Токен аккаунта не найден");
    }

    const [user, customers] = await Promise.all([this.me(), this.customers()]);
    return { user, customers };
  }

  async logout(): Promise<void> {
    // An account session ends together with the customer sessions opened from it;
    // /customers/logout is only for a masked one, which has no account behind it.
    const endpoint = this.getAccountToken()
      ? ACCOUNT_ENDPOINTS.LOGOUT
      : isMaskedCustomerToken(this.getCustomerToken())
        ? ACCOUNT_ENDPOINTS.CUSTOMER_LOGOUT
        : null;

    if (endpoint) {
      try {
        await httpClient.delete(endpoint);
      } catch {
        // Локальную сессию всё равно сбрасываем ниже
      }
    }

    this.clearAuth();
  }

  // === CURRENT ACCOUNT ===

  async me(): Promise<AuthUser> {
    return httpClient.get<AuthUser>(ACCOUNT_ENDPOINTS.ME);
  }

  async customers(): Promise<Customer[]> {
    const response = await httpClient.get<CustomersList>(ACCOUNT_ENDPOINTS.ME_CUSTOMERS);
    return response.items;
  }

  async forgotPassword(email: string): Promise<void> {
    await httpClient.post<void, ForgotPasswordPayload>(ACCOUNT_ENDPOINTS.ME_FORGOT_PASSWORD, {
      email,
    }, false);
  }

  async restorePassword(payload: RestorePasswordPayload): Promise<void> {
    await httpClient.post<void, RestorePasswordPayload>(
      ACCOUNT_ENDPOINTS.ME_RESTORE_PASSWORD,
      payload,
      false
    );
  }

  // === UTILITIES ===

  isAuthenticated(): boolean {
    return !!this.getAccountToken();
  }

  getCustomerToken(): string | null {
    return getAuthItem(CUSTOMER_TOKEN_KEY);
  }

  getAccountToken(): string | null {
    return getAuthItem(ACCOUNT_TOKEN_KEY);
  }

  getSelectedCustomerId(): number | null {
    const value = getAuthItem(SELECTED_CUSTOMER_ID_KEY);
    if (!value) {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  hasActiveCustomerSession(): boolean {
    return this.getSelectedCustomerId() != null && !!this.getCustomerToken();
  }

  clearSelectedCustomer(): void {
    removeAuthItem(SELECTED_CUSTOMER_ID_KEY);
    httpClient.removeCustomerToken();
  }

  async deactivateCustomerSession(): Promise<void> {
    // A customer session opened from the account cannot sign out on its own (403): it is just dropped.
    if (isMaskedCustomerToken(this.getCustomerToken())) {
      try {
        await httpClient.delete(ACCOUNT_ENDPOINTS.CUSTOMER_LOGOUT);
      } catch {
        // Локальную customer-сессию всё равно сбрасываем ниже
      }
    }

    this.clearSelectedCustomer();
  }

  clearAuth(): void {
    httpClient.clearTokens();
    removeAuthItem(SELECTED_CUSTOMER_ID_KEY);
    removeAuthItem("authToken");
    removeAuthItem("accountAuthToken");
    localStorage.removeItem("authToken");
    localStorage.removeItem("accountAuthToken");
  }
}

export const accountsApi = new AccountsApi();
export const authApi = accountsApi;
